import { Invoiceable, InvoiceLine, Tax, Party , ExtraAmount, RectificativeInfo, Invoice, Budget, DeliveryNote, Proforma, RecInv, Ticket, RepresentationData, ExtraFieldValue} from "@/modules/facturae/domain/Invoiceable";
import { InvoiceableRepository } from "@/modules/facturae/domain/InvoiceableRepository";
import { CommandDispatcher } from "@/modules/shared/domain/CommandDispatcher";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";
import { query_dispatcher_get, sync_command_dispatcher, async_command_dispatcher } from "@/api/client";
import axios from "axios";
import { EmailTemplate } from "../domain/Configuration";

export function objectToDict(obj: any): Record<string, any> {
    const dict: Record<string, any> = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // Recursively convert nested object
                dict[key] = objectToDict(value);
            } else {
                dict[key] = value;
            }
        }
    }

    return dict;
}

export function dataToLines(data): InvoiceLine[] {
    let lines = []
    if (data.invoice_lines) {
        lines = data.invoice_lines.map(line =>
            new InvoiceLine({
                description: line.description,
                quantity: line.quantity,
                unit_price_without_tax: line.unit_price_without_tax,
                total_cost: line.total_cost,
                gross_amount: line.gross_amount,
                taxes: line.taxes.map(tax => new Tax(tax.rate, tax.base, tax.amount, tax.recargo_rate, tax.recargo_amount)),
                taxes_withheld: line.taxes_withheld.map(tax => new Tax(tax.rate, tax.base, tax.amount)),
                id: line.id,
                product_code: line.product_code,
                special_taxable_event: line.special_taxable_event,
                additional_information: line.additional_information,
                discounts: line.discounts.map(d => new ExtraAmount(d.reason, d.amount, d.rate)),
                additional_fields: line.additional_fields ? line.additional_fields.map((f: any) => new ExtraFieldValue(f.name, f.value, f.key)) : []
            })
        );
        for(let line in lines){
            lines[line].selected_retention = 'na'
            if (lines[line].taxes_withheld.length > 0){
                lines[line].selected_retention = lines[line].taxes_withheld[0].rate //autocomplete selected retention
            }
            lines[line].selected_tax = lines[line].taxes[0].rate //autocomplete selected tax
            if (lines[line].taxes[0].recargo_rate){
                lines[line].selected_tax = lines[line].taxes[0].recargo_rate
            }
            lines[line].selected_tax_event = 'N/A'
            if (lines[line].special_taxable_event){
                let code = lines[line].special_taxable_event.code
                if (code == '01'){
                    if (lines[line].special_taxable_event.reason.includes('sujeto pasivo')){
                        lines[line].selected_tax_event = 'ISP'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('intracomunitaria')){
                        lines[line].selected_tax_event = 'INTRA.'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('exportaci')){
                        lines[line].selected_tax_event = 'EXPORT.'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Art. 50')){
                        lines[line].selected_tax_event = 'Art. 50'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Art. 11')){
                        lines[line].selected_tax_event = 'Art. 11'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Art. 12')){
                        lines[line].selected_tax_event = 'Art. 12'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Art. 13')){
                        lines[line].selected_tax_event = 'Art. 13'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Art. 25')){
                        lines[line].selected_tax_event = 'Art. 25'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Art. 47')){
                        lines[line].selected_tax_event = 'Art. 47'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Art. 110')){
                        lines[line].selected_tax_event = 'Art. 110'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('exent') && !lines[line].special_taxable_event.reason.includes('IGIC')){ //todos los anteriores también incluyen exent
                        lines[line].selected_tax_event = 'EX'
                    }
                    else if(lines[line].special_taxable_event.reason.includes('Operación exenta de IGIC.')){
                        lines[line].selected_tax_event = 'Otros'
                    }

                }
                else if (code == '02'){
                    lines[line].selected_tax_event = 'NS'
                }
            }
        }
        lines.sort((a, b) => {
            if (a.id === undefined && b.id === undefined) return 0; // Ambos no tienen ID, no cambiar su orden
            if (a.id === undefined) return 1; // 'a' no tiene ID, mover al final
            if (b.id === undefined) return -1; // 'b' no tiene ID, mover al final
            return a.id - b.id; // Ambos tienen ID, ordenar por ID
            });
    }
    return lines
}

export function dataToParty(data:any, customer:boolean): Party {
    let party =  new Party({
        id: data.id,
        company_id: data.company_id,
        cif: data.cif,
        post_code: data.post_code,
        type: data.type,
        name: data.name,
        first_surname: data.first_surname,
        second_surname: data.second_surname,
        business_name: data.business_name,
        trade_name: data.trade_name,
        address: data.address,
        city: data.city,
        province: data.province,
        country: data.country,
        email: data.email,
        phone: data.phone,
        website: data.website,
        bank_account_number: data.bank_account_number,
        bic: data.bic,
        mandate_id: data.mandate_id,
        mandate_signature_date: data.mandate_signature_date,
        debit_account: data.debit_account,
        payment_means: data.payment_means,
        representation_data: data.representation_data ? RepresentationData.fromPrimitive(data.representation_data) : new RepresentationData(),
        payment_information: data.payment_information,
        pdf_language: data.pdf_language,
        timestamp_modified: data.timestamp_modified
    })
    if (customer) {
        party.DIRe = data.DIRe
        party.accounting_office = data.accounting_office
        party.management_body = data.management_body
        party.processing_unit = data.processing_unit
        party.proposing_body = data.proposing_body
        // party.mandate_id = data.mandate_id
        party.mandate_signature_date = data.mandate_signature_date
        party.payment_means = data.payment_means
        party.payment_information = data.payment_information
        party.pdf_language = data.pdf_language
    }
    return party
}

export class ApiInvoiceableRepository implements InvoiceableRepository {

    command_dispatcher: CommandDispatcher
    query_dispatcher: QueryDispatcher
    async_command_dispatcher: CommandDispatcher
    constructor() {
        this.command_dispatcher = sync_command_dispatcher
        this.query_dispatcher = query_dispatcher_get
        this.async_command_dispatcher = async_command_dispatcher
    }
    // tax(rate: number, base: number, amount: number) {
    // line(description: string, quantity: number, unit_price_without_tax: number, total_cost: number, gross_amount: number, taxes: Tax[], id?: number) {
    // inv(id: number, total_amount: number, invoice_lines: InvoiceLine[], other_amounts?: number) {
    api_to_domain(inv_data) {
        let lines = dataToLines(inv_data)
        let discounts = []
        if (inv_data.general_discounts){
            discounts = inv_data.general_discounts.map(discount => new ExtraAmount(discount.reason, discount.amount, discount.rate))
        }
        let supplier = null
        if (inv_data.supplier) {
            supplier = dataToParty(inv_data.supplier, false)
        }
        let customer = null
        if (inv_data.customer) {
            customer = dataToParty(inv_data.customer, true)
        }

        if (inv_data.type == 'budget') {
            return new Budget({
                id: inv_data.id,
                company_id: inv_data.company_id,
                total_amount: inv_data.total_amount,
                total_to_pay: inv_data.total_to_pay,
                invoice_number: inv_data.invoice_number,
                invoice_date: inv_data.invoice_date,
                lines: lines,
                other_amounts: inv_data.other_amounts,
                operation_date: inv_data.operation_date,
                supplier: supplier,
                customer: customer,
                state: inv_data.state,
                invoice_due_date: inv_data.invoice_due_date,
                external_invoice_number: inv_data.external_invoice_number,
                start_date: inv_data.start_date,
                end_date: inv_data.end_date,
                retention_concept: inv_data.retention_concept,
                retention_amount: inv_data.retention_amount!=0?inv_data.retention_amount:undefined,
                kind: inv_data.kind,
                total_gross_amount: 0,
                total_lines_discounts: 0,
                total_gross_amount_before_tax: 0,
                total_tax_amount: 0,
                total_withheld_amount: 0,
                total_taxes: [],
                total_taxes_withheld: [],
                general_discounts: discounts,
                general_surcharges: [],
                reimbursable_expense_concept: inv_data.reimbursable_expense_concept,
                reimbursable_amounts: inv_data.reimbursable_amounts,
                signing_time: inv_data.signing_time,
                sequence_id: inv_data.sequence_id,
                predicted_invoice_number: inv_data.predicted_invoice_number,
                payment_state: inv_data.payment_state,
                accounting_document_id: inv_data.accounting_document_id,
                source_odoc_id: inv_data.source_odoc_id,
                rectificative: inv_data.rectificative,
                rectificative_info: null,
                predicted_rectificative_invoice_number: inv_data.predicted_rectificative_invoice_number,
                tax_type: inv_data.tax_type,
                additional_info: inv_data.additional_info,
                free_notes: inv_data.free_notes,
                payment_means: null,
                payment_information: '',
                recurring_invoice_contract_id: null,
                is_from_recurring_contract: null,
                related_invoiceable_ids: inv_data.related_invoiceable_ids,
                related_invoiceables_type: inv_data.related_invoiceables_type,
                flag_payment_means_modified: false,
                flag_payment_information_modified: false,
                qr_url: '',
                pending_email_send: inv_data.pending_email_send,
                pending_email_error: inv_data.pending_email_error
            })
        } else if (inv_data.type === 'delivery_note') {
            return new DeliveryNote({
                id: inv_data.id,
                company_id: inv_data.company_id,
                total_amount: inv_data.total_amount,
                total_to_pay: inv_data.total_to_pay,
                invoice_number: inv_data.invoice_number,
                invoice_date: inv_data.invoice_date,
                lines: lines,
                other_amounts: inv_data.other_amounts,
                operation_date: inv_data.operation_date,
                supplier: supplier,
                customer: customer,
                state: inv_data.state,
                invoice_due_date: inv_data.invoice_due_date,
                external_invoice_number: inv_data.external_invoice_number,
                start_date: inv_data.start_date,
                end_date: inv_data.end_date,
                retention_concept: inv_data.retention_concept,
                retention_amount: inv_data.retention_amount!=0?inv_data.retention_amount:undefined,
                kind: inv_data.kind,
                total_gross_amount: 0,
                total_lines_discounts: 0,
                total_gross_amount_before_tax: 0,
                total_tax_amount: 0,
                total_withheld_amount: 0,
                total_taxes: [],
                total_taxes_withheld: [],
                general_discounts: discounts,
                general_surcharges: [],
                reimbursable_expense_concept: inv_data.reimbursable_expense_concept,
                reimbursable_amounts: inv_data.reimbursable_amounts,
                signing_time: inv_data.signing_time,
                sequence_id: inv_data.sequence_id,
                predicted_invoice_number: inv_data.predicted_invoice_number,
                payment_state: inv_data.payment_state,
                accounting_document_id: inv_data.accounting_document_id,
                source_odoc_id: inv_data.source_odoc_id,
                rectificative: inv_data.rectificative,
                rectificative_info: null,
                predicted_rectificative_invoice_number: inv_data.predicted_rectificative_invoice_number,
                tax_type: inv_data.tax_type,
                additional_info: inv_data.additional_info,
                free_notes: inv_data.free_notes,
                payment_means: null,
                payment_information: '',
                recurring_invoice_contract_id: null,
                is_from_recurring_contract: null,
                related_invoiceable_ids: inv_data.related_invoiceable_ids,
                related_invoiceables_type: inv_data.related_invoiceables_type,
                flag_payment_means_modified: false,
                flag_payment_information_modified: false,
                qr_url: '',
                pending_email_send: inv_data.pending_email_send,
                pending_email_error: inv_data.pending_email_error
            })
        } else if (inv_data.type === 'proforma') {
            return new Proforma({
                id: inv_data.id,
                company_id: inv_data.company_id,
                total_amount: inv_data.total_amount,
                total_to_pay: inv_data.total_to_pay,
                invoice_number: inv_data.invoice_number,
                invoice_date: inv_data.invoice_date,
                lines: lines,
                other_amounts: inv_data.other_amounts,
                operation_date: inv_data.operation_date,
                supplier: supplier,
                customer: customer,
                state: inv_data.state,
                invoice_due_date: inv_data.invoice_due_date,
                external_invoice_number: inv_data.external_invoice_number,
                start_date: inv_data.start_date,
                end_date: inv_data.end_date,
                retention_concept: inv_data.retention_concept,
                retention_amount: inv_data.retention_amount!=0?inv_data.retention_amount:undefined,
                kind: inv_data.kind,
                total_gross_amount: 0,
                total_lines_discounts: 0,
                total_gross_amount_before_tax: 0,
                total_tax_amount: 0,
                total_withheld_amount: 0,
                total_taxes: [],
                total_taxes_withheld: [],
                general_discounts: discounts,
                general_surcharges: [],
                reimbursable_expense_concept: inv_data.reimbursable_expense_concept,
                reimbursable_amounts: inv_data.reimbursable_amounts,
                signing_time: inv_data.signing_time,
                sequence_id: inv_data.sequence_id,
                predicted_invoice_number: inv_data.predicted_invoice_number,
                payment_state: inv_data.payment_state,
                accounting_document_id: inv_data.accounting_document_id,
                source_odoc_id: inv_data.source_odoc_id,
                rectificative: inv_data.rectificative,
                rectificative_info: null,
                predicted_rectificative_invoice_number: inv_data.predicted_rectificative_invoice_number,
                tax_type: inv_data.tax_type,
                additional_info: inv_data.additional_info,
                free_notes: inv_data.free_notes,
                payment_means: null,
                payment_information: '',
                recurring_invoice_contract_id: null,
                is_from_recurring_contract: null,
                related_invoiceable_ids: inv_data.related_invoiceable_ids,
                related_invoiceables_type: inv_data.related_invoiceables_type,
                flag_payment_means_modified: false,
                flag_payment_information_modified: false,
                qr_url: '',
                pending_email_send: inv_data.pending_email_send,
                pending_email_error: inv_data.pending_email_error
            })
        }
         else if (inv_data.type === 'recurring_invoice_contract') {
            return new RecInv({
                id: inv_data.id,
                company_id: inv_data.company_id,
                total_amount: inv_data.total_amount,
                total_to_pay: inv_data.total_to_pay,
                invoice_number: '',
                invoice_date: '',
                lines: lines,
                other_amounts: inv_data.other_amounts,
                operation_date: inv_data.operation_date,
                supplier: supplier,
                customer: customer,
                state: '',
                invoice_due_date: '',
                external_invoice_number: '',
                start_date: '',
                end_date: '',
                retention_concept: inv_data.retention_concept,
                retention_amount: inv_data.retention_amount!=0?inv_data.retention_amount:undefined,
                kind: inv_data.kind,
                total_gross_amount: 0,
                total_lines_discounts: 0,
                total_gross_amount_before_tax: 0,
                total_tax_amount: 0,
                total_withheld_amount: 0,
                total_taxes: [],
                total_taxes_withheld: [],
                general_discounts: discounts,
                general_surcharges: [],
                reimbursable_expense_concept: inv_data.reimbursable_expense_concept,
                reimbursable_amounts: inv_data.reimbursable_amounts,
                signing_time: '',
                sequence_id: inv_data.sequence_id,
                predicted_invoice_number: inv_data.predicted_invoice_number,
                payment_state: '',
                accounting_document_id: null,
                source_odoc_id: null,
                rectificative: false,
                rectificative_info: null,
                predicted_rectificative_invoice_number: '',
                tax_type: inv_data.tax_type,
                additional_info: inv_data.additional_info,
                free_notes: '',
                payment_means: inv_data.payment_means,
                payment_information: inv_data.payment_information,
                recurring_invoice_contract_id: inv_data.recurring_invoice_contract_id,
                is_from_recurring_contract: inv_data.is_for_recurring_contracts,
                related_invoiceable_ids: inv_data.related_invoiceable_ids,
                related_invoiceables_type: inv_data.related_invoiceables_type,
                flag_payment_means_modified: false,
                flag_payment_information_modified: false,
                qr_url: '',
                pending_email_send: inv_data.pending_email_send,
                pending_email_error: inv_data.pending_email_error
            })
        }
        else if(inv_data.type === 'ticket') {
            return new Ticket({
                id: inv_data.id,
                company_id: inv_data.company_id,
                total_amount: inv_data.total_amount,
                total_to_pay: inv_data.total_to_pay,
                invoice_number: inv_data.invoice_number,
                invoice_date: inv_data.invoice_date,
                lines: lines,
                other_amounts: inv_data.other_amounts,
                operation_date: inv_data.operation_date,
                supplier: supplier,
                customer: customer,
                state: inv_data.state,
                invoice_due_date: inv_data.invoice_due_date,
                external_invoice_number: inv_data.external_invoice_number,
                start_date: inv_data.start_date,
                end_date: inv_data.end_date,
                retention_concept: inv_data.retention_concept,
                retention_amount: inv_data.retention_amount!=0?inv_data.retention_amount:undefined,
                kind: inv_data.kind,
                total_gross_amount: 0,
                total_lines_discounts: 0,
                total_gross_amount_before_tax: 0,
                total_tax_amount: 0,
                total_withheld_amount: 0,
                total_taxes: [],
                total_taxes_withheld: [],
                general_discounts: discounts,
                general_surcharges: [],
                reimbursable_expense_concept: inv_data.reimbursable_expense_concept,
                reimbursable_amounts: inv_data.reimbursable_amounts,
                signing_time: inv_data.signing_time,
                sequence_id: inv_data.sequence_id,
                predicted_invoice_number: inv_data.predicted_invoice_number,
                payment_state: inv_data.payment_state,
                accounting_document_id: inv_data.accounting_document_id,
                source_odoc_id: inv_data.source_odoc_id,
                rectificative: false,
                rectificative_info: null,
                predicted_rectificative_invoice_number: inv_data.predicted_rectificative_invoice_number,
                tax_type: inv_data.tax_type,
                additional_info: inv_data.additional_info,
                free_notes: inv_data.free_notes,
                payment_means: inv_data.payment_means,
                payment_information: inv_data.payment_information,
                recurring_invoice_contract_id: null,
                is_from_recurring_contract: null,
                related_invoiceable_ids: inv_data.related_invoiceable_ids,
                related_invoiceables_type: inv_data.related_invoiceables_type,
                flag_payment_means_modified: inv_data.flag_payment_means_modified,
                flag_payment_information_modified: inv_data.flag_payment_information_modified,
                qr_url: inv_data.verifactu_data ? inv_data.verifactu_data.qr_url : null,
                pending_email_send: inv_data.pending_email_send,
                pending_email_error: inv_data.pending_email_error
            })
        }
        else {
            let rectificative_info = null
            if (inv_data.rectificative && inv_data.rectificative_info){
                rectificative_info = new RectificativeInfo(inv_data.rectificative_info.reason, inv_data.rectificative_info.invoice_id, inv_data.rectificative_info.invoice_number, inv_data.rectificative_info.invoice_date)
            }
            return new Invoice({
                id: inv_data.id,
                company_id: inv_data.company_id,
                total_amount: inv_data.total_amount,
                total_to_pay: inv_data.total_to_pay,
                invoice_number: inv_data.invoice_number,
                invoice_date: inv_data.invoice_date,
                lines: lines,
                other_amounts: inv_data.other_amounts,
                operation_date: inv_data.operation_date,
                supplier: supplier,
                customer: customer,
                state: inv_data.state,
                invoice_due_date: inv_data.invoice_due_date,
                external_invoice_number: inv_data.external_invoice_number,
                start_date: inv_data.start_date,
                end_date: inv_data.end_date,
                retention_concept: inv_data.retention_concept,
                retention_amount: inv_data.retention_amount!=0?inv_data.retention_amount:undefined,
                kind: inv_data.kind,
                total_gross_amount: 0,
                total_lines_discounts: 0,
                total_gross_amount_before_tax: 0,
                total_tax_amount: 0,
                total_withheld_amount: 0,
                total_taxes: [],
                total_taxes_withheld: [],
                general_discounts: discounts,
                general_surcharges: [],
                reimbursable_expense_concept: inv_data.reimbursable_expense_concept,
                reimbursable_amounts: inv_data.reimbursable_amounts,
                signing_time: inv_data.signing_time,
                sequence_id: inv_data.sequence_id,
                predicted_invoice_number: inv_data.predicted_invoice_number,
                payment_state: inv_data.payment_state,
                accounting_document_id: inv_data.accounting_document_id,
                source_odoc_id: inv_data.source_odoc_id,
                rectificative: inv_data.rectificative,
                rectificative_info: rectificative_info,
                predicted_rectificative_invoice_number: inv_data.predicted_rectificative_invoice_number,
                tax_type: inv_data.tax_type,
                additional_info: inv_data.additional_info,
                free_notes: inv_data.free_notes,
                payment_means: inv_data.payment_means,
                payment_information: inv_data.payment_information,
                recurring_invoice_contract_id: null,
                is_from_recurring_contract: inv_data.is_from_recurring_contract,
                related_invoiceable_ids: inv_data.related_invoiceable_ids,
                related_invoiceables_type: inv_data.related_invoiceables_type,
                flag_payment_means_modified: inv_data.flag_payment_means_modified,
                flag_payment_information_modified: inv_data.flag_payment_information_modified,
                qr_url: inv_data.verifactu_data ? inv_data.verifactu_data.qr_url : null,
                pending_email_send: inv_data.pending_email_send,
                pending_email_error: inv_data.pending_email_error
            })
        }
    }
    domain_to_api(inv) {
        let data = JSON.parse(JSON.stringify(objectToDict(inv)));
        data['invoice_lines'] = [...data['lines']]
        delete data['lines']
        delete data['related_invoiceable_ids']
        for(let line in data['invoice_lines']){
            delete data['invoice_lines'][line]['selected_retention']
            delete data['invoice_lines'][line]['selected_tax']
        }
        if (data['retention_amount'] == ''){
            data['retention_amount'] = null
        }
        // delete data['supplier']['representation_data']
        // delete data['customer']['representation_data']
        return data
    }

    async duplicate_as_draft(ids: Array<number>): Promise<any> {
        let res = await this.command_dispatcher.dispatch('duplicate.as.draft', { inv_ids: ids })
        return res.output
    }
    async send_to_accounting(ids: Array<number>): Promise<any> {
        return await this.async_command_dispatcher.dispatch('send.invoices.to.accounting', { inv_ids: ids })
    }
    async generate_invoice_from_invoiceable(ids: Array<number>, order_by_invoice_number: boolean): Promise<any> {
        let res = await this.command_dispatcher.dispatch('generate.invoice.from.invoiceable', { invoiceables_ids:ids , order_by_invoice_number:order_by_invoice_number })
        return res.output
    }
    async generate_remitance(ids: Array<number>, selectedRemittanceType: string): Promise<any> {
        let res = await this.command_dispatcher.dispatch("generate.invoices.remittance", { inv_ids: ids, remittance_type: selectedRemittanceType })
        return res.output
    }
    async find_by_id(id): Promise<Invoiceable | null> {
        let inv_data = await this.query_dispatcher.dispatch('get.static.invoice', { inv_id: id })
        if (inv_data){
            let res =  this.api_to_domain(inv_data)
            return res
        }
        return null
    }

    async filter_invoices(args): Promise<{results: Invoiceable[], count: number}> {
        let inv_data = await this.query_dispatcher.dispatch('get.facture.e.list', args)
        let count = inv_data.count
        inv_data = inv_data.results || []
        let inv_list = []
        for (let inv in inv_data) {
            inv_list.push(this.api_to_domain(inv_data[inv]))
        }
        if(!count){
            count = inv_list.length
        }
        return {results:inv_list, count: count}
    }

    async get_invoiceable_id_list(args): Promise<any> {
        let res = await this.query_dispatcher.dispatch('get.facture.e.id.list', args)
        return res.id_list
    }

    async save(inv) {
        let inv_data = this.domain_to_api(inv)
        let command = 'save.invoice'
        if (inv.type == 'budget') {
            command = 'save.budget'
        } else if (inv.type == 'delivery_note') {
            command = 'save.delivery_note'
        } else if (inv.type == 'proforma') {
            command = 'save.proforma'
        } else if (inv.type == 'ticket') {
            command = 'save.ticket'
        }
        let res = await this.command_dispatcher.dispatch(command, { inv: inv_data })
        const output = res['output']
        // Si hay un error, lanzar excepción para que sea manejada por el caller
        if (output['error']) {
            throw new Error(`${output['error_type'] || 'SAVE_ERROR'}|${output['message']}`)
        }
        return output['inv_id']
    }

    async import_lines_from_excel(id, path): Promise<any> {
        return await this.command_dispatcher.dispatch('import.lines.from.excel', { inv_id: id, excel_path: path })
    }

    async mock_find_by_id(id): Promise<Invoiceable | null> {

        return new Invoiceable({
            id: 1,
            company_id: 200,
            total_amount: 605,
            total_to_pay: 605,
            invoice_number: "2020/0001",
            invoice_date: "2020-01-01",
            lines: [
                new InvoiceLine({ description: "line 1", quantity: 1, unit_price_without_tax: 100, total_cost: 100, gross_amount: 100, taxes: [new Tax(0.21, 100, 21)], taxes_withheld: [], id: 1 }),
                new InvoiceLine({ description: "line 2", quantity: 1, unit_price_without_tax: 100, total_cost: 100, gross_amount: 100, taxes: [new Tax(0.21, 100, 21)], taxes_withheld: [], id: 2 }),
                new InvoiceLine({ description: "line 3", quantity: 1, unit_price_without_tax: 100, total_cost: 100, gross_amount: 100, taxes: [new Tax(0.21, 100, 21)], taxes_withheld: [], id: 3 }),
                new InvoiceLine({ description: "line 4", quantity: 1, unit_price_without_tax: 100, total_cost: 100, gross_amount: 100, taxes: [new Tax(0.21, 100, 21)], taxes_withheld: [], id: 4 }),
                new InvoiceLine({ description: "line 5", quantity: 1, unit_price_without_tax: 100, total_cost: 100, gross_amount: 100, taxes: [new Tax(0.21, 100, 21)], taxes_withheld: [], id: 5 }),
            ],
            other_amounts: 0,
            supplier: new Party({
                id: 1,
                company_id: 200,
                cif: "12345678Z",
                post_code: "12345",
                name: "Supplier",
                first_surname: "Supplier last name",
                address: "Supplier address",
                city: "Supplier city",
                province: "Supplier province"
            }),
            customer: new Party({
                id: 2,
                company_id: 200,
                cif: "12345678Z",
                post_code: "12345",
                type: "fisica",
                name: "Customer name",
                first_surname: "Customer last name",
                address: "Customer address",
                city: "Customer city",
                province: "Customer province"
            })
        })
    }

    supplier_from_current_company(currentCompany) {
        let supplier = new Party({
            id: 0,
            company_id: currentCompany.id,
            cif: "",
            post_code: ""
        })
        if (currentCompany.configuration && currentCompany.configuration.company_as_party){
            let cap = currentCompany.configuration.company_as_party
            supplier.business_name = cap.business_name ? cap.business_name : ""
            supplier.trade_name = cap.trade_name ? cap.trade_name : ""
            supplier.name = cap.name ? cap.name : ""
            supplier.first_surname = cap.first_surname ? cap.first_surname : ""
            supplier.second_surname = cap.second_surname ? cap.second_surname : ""
            supplier.type = cap.type
            supplier.cif = cap.cif
            supplier.address = cap.address
            supplier.city = cap.city
            supplier.province = cap.province
            supplier.email = cap.email
            supplier.phone = cap.phone
            supplier.website = cap.website
            supplier.post_code = cap.post_code
            supplier.bank_account_number = cap.bank_account_number
            supplier.bic = cap.bic
            supplier.mandate_id = cap.mandate_id ? cap.mandate_id : ''
            supplier.representation_data = cap.representation_data ? RepresentationData.fromPrimitive(cap.representation_data) : new RepresentationData()
        }
        return supplier
    }

    create_empty_invoiceable(company_id, currentCompany, next_number, next_rectificative_number, tax_type = 'iva', invoiceable_type='invoice') {
        let first_tax = 0.21
        if(!tax_type.includes('iva') && tax_type.includes('igic')){
            first_tax = 0.07
        }
        let empty_line = new InvoiceLine({
            description: "",
            quantity: 0,
            unit_price_without_tax: 0,
            total_cost: 0,
            gross_amount: 0,
            taxes: [new Tax(first_tax, 0, 0)],
            selected_tax: first_tax
        })
        // empty_line.discounts = [new ExtraAmount('Descuento', 0, 0)]
        let supplier = null
        let si = null;
        if (currentCompany) {
            supplier = this.supplier_from_current_company(currentCompany)
        } else{
            supplier = new Party({
                id: 0,
                company_id: company_id,
                cif: "",
                post_code: ""
            })
        }
        // Para presupuestos, proformas y albaranes, establecer fecha de hoy al crear
        // Para facturas y tickets, la fecha se setea al firmar
        let today_date = new Date().toISOString().split('T')[0]
        let invoice_date = ''
        if (invoiceable_type == 'budget') {
            si = new Budget({
                id: null,
                company_id: company_id,
                total_amount: 0,
                total_to_pay: 0,
                invoice_number: "",
                invoice_date: today_date,
                lines: [empty_line],
                other_amounts: 0,
                operation_date: undefined,
                supplier: supplier,
                customer: new Party({
                    id: 0,
                    company_id: company_id,
                    cif: "",
                    post_code: ""
                })
            })
        } else if (invoiceable_type === 'delivery_note') {
            si = new DeliveryNote({
                id: null,
                company_id: company_id,
                total_amount: 0,
                total_to_pay: 0,
                invoice_number: "",
                invoice_date: today_date,
                lines: [empty_line],
                other_amounts: 0,
                operation_date: undefined,
                supplier: supplier,
                customer: new Party({
                    id: 0,
                    company_id: company_id,
                    cif: "",
                    post_code: ""
                })
            })
        } else if (invoiceable_type === 'proforma') {
            si = new Proforma({
                id: null,
                company_id: company_id,
                total_amount: 0,
                total_to_pay: 0,
                invoice_number: "",
                invoice_date: today_date,
                lines: [empty_line],
                other_amounts: 0,
                operation_date: undefined,
                supplier: supplier,
                customer: new Party({
                    id: 0,
                    company_id: company_id,
                    cif: "",
                    post_code: ""
                })
            })
        } else if (invoiceable_type === 'recurring_invoice_contract') {
            si = new RecInv({
                id: null,
                company_id: company_id,
                total_amount: 0,
                total_to_pay: 0,
                invoice_number: "",
                invoice_date: invoice_date,
                lines: [empty_line],
                other_amounts: 0,
                operation_date: undefined,
                supplier: supplier,
                customer: new Party({
                    id: 0,
                    company_id: company_id,
                    cif: "",
                    post_code: ""
                }),
                state: ''
            })
        } else if (invoiceable_type === 'ticket') {
            //Por si volvemos a Zerocoma (dios no lo quiera()s)
            si = new Ticket({
                id: null,
                company_id: company_id,
                total_amount: 0,
                total_to_pay: 0,
                invoice_number: "",
                invoice_date: invoice_date,
                lines: [empty_line],
                other_amounts: 0,
                operation_date: undefined,
                supplier: supplier,
                customer: new Party({
                    id: 0,
                    company_id: company_id,
                    cif: "00000000Z",
                    post_code: ""
                }),
                state: 'draft'
            })
            // si = new Ticket(null, company_id, 0, 0, "", invoice_date, [empty_line], 0, supplier, null, 'draft')
        } else {
            si = new Invoice({
                id: null,
                company_id: company_id,
                total_amount: 0,
                total_to_pay: 0,
                invoice_number: "",
                invoice_date: invoice_date,
                lines: [empty_line],
                other_amounts: 0,
                operation_date: undefined,
                supplier: supplier,
                customer: new Party({
                    id: 0,
                    company_id: company_id,
                    cif: "",
                    post_code: ""
                }),
                state: 'draft',
                invoice_due_date: undefined
            })
        }
        si.predicted_invoice_number = next_number
        si.predicted_rectificative_invoice_number = next_rectificative_number
        if (tax_type == 'igic'){
            si.tax_type = 'igic'
        }
        else{
            si.tax_type = 'iva'
        }
        
        return si
    }

    async download_xml(id) {
        return await this.async_command_dispatcher.dispatch('create.xml.from.invoice', { inv_id: id })
    }

    async download_pdf(id: any): Promise<any> {
        return await this.async_command_dispatcher.dispatch('downalod_e_invoice_pdf', { inv_id: id, force_download: true })

    }

    async download_pdfs(ids: Array<any>): Promise<any> {
        return await this.async_command_dispatcher.dispatch_with_result('massive.download.pdfs', { inv_ids: ids })

    }

    async sign(id): Promise<any> {
        console.log("sign")
        let res = await this.async_command_dispatcher.dispatch("sign.static.invoice.xml", { inv_id: id })
        return res
    }

    async issue(id): Promise<any> {
        console.log("issue")
        let res = await this.async_command_dispatcher.dispatch("issue.invoiceable", { inv_id: id })
        return res
    }

    async unsign(id): Promise<boolean> {
        try{
            let res = await this.command_dispatcher.dispatch("unsign.invoice", { inv_id: id })
            if (res.output.error) {
                return false
            }
            else {
                return true
            }
        }
        catch(e){
            return false
        }
        
    }

    async send(id, send_to): Promise<any> {
        return await this.async_command_dispatcher.dispatch("send.factura.e.xml", { inv_id: id, send_to:send_to })
    }

    async send_invoice_email(id, companydb_id, email): Promise<any> {
        let args = { inv_id: id, company_id: companydb_id, to: email.to , subject: email.subject, body: email.body }
        return await this.async_command_dispatcher.dispatch("send.invoice.via.mail", args)
    }

    async send_invoice_accounting(id) : Promise<any> {
        return await this.async_command_dispatcher.dispatch("send.invoice.to.accounting", { inv_id: id })
    }

    async get_customer_list(company_id:string|number, search_term: string): Promise<Party[]> {
        let res = await this.query_dispatcher.dispatch('get.facture.e.customers', { company_id: company_id, search_term: search_term })
        let customerList = [new Party({
            id: -1,
            company_id: company_id,
            cif: "",
            post_code: ""
        })]
        if (res) {
            for (let customer in res) {
                let emptyParty = new Party({
                    id: 0,
                    company_id: res[customer].company_id,
                    cif: "",
                    post_code: ""
                }) 
                emptyParty.id = res[customer].id
                emptyParty.cif = res[customer].cif
                emptyParty.post_code = res[customer].post_code
                emptyParty.type = res[customer].type
                emptyParty.name = res[customer].name
                emptyParty.first_surname = res[customer].first_surname
                emptyParty.second_surname = res[customer].second_surname
                emptyParty.business_name = res[customer].business_name
                emptyParty.trade_name = res[customer].trade_name
                emptyParty.address = res[customer].address
                emptyParty.city = res[customer].city
                emptyParty.province = res[customer].province
                emptyParty.country = res[customer].country
                emptyParty.email = res[customer].email
                emptyParty.phone = res[customer].phone
                emptyParty.website = res[customer].website
                emptyParty.DIRe = res[customer].DIRe
                emptyParty.accounting_office = res[customer].accounting_office
                emptyParty.management_body = res[customer].management_body
                emptyParty.processing_unit = res[customer].processing_unit
                emptyParty.proposing_body = res[customer].proposing_body
                emptyParty.bank_account_number = res[customer].bank_account_number
                emptyParty.bic = res[customer].bic
                emptyParty.mandate_id = res[customer].mandate_id
                emptyParty.mandate_signature_date = res[customer].mandate_signature_date
                emptyParty.debit_account = res[customer].debit_account
                emptyParty.payment_means = res[customer].payment_means
                emptyParty.payment_information = res[customer].payment_information
                emptyParty.pdf_language = res[customer].pdf_language
                emptyParty.timestamp_modified = res[customer].timestamp_modified
                customerList.push(emptyParty)
            }
        }
        return customerList
    }

    async download_registred_invoices(): Promise<void> {
        await this.command_dispatcher.dispatch('download.registered.invoices', {})
    }

    async reject_invoiceable(id: number, reject_comment: string): Promise<string> {
        let res = await this.command_dispatcher.dispatch('reject.invoice', { inv_id: id, comment: reject_comment })
        return res.output
    }

    async delete_selected_items(selected_ids: Array<number>): Promise<Object> {
        try{
            await this.command_dispatcher.dispatch('delete.static.invoices', { inv_ids: selected_ids })
            return true
        }
        catch(e){
            return false
        }
    }
    async pay_downloaded_invoice(id:number): Promise<string> {
        let res = await this.command_dispatcher.dispatch('pay.invoice', { inv_id: id })
        return res.output
    }

    async save_third(party:Party): Promise<{ status: string, message?: string|Party }> {
        let res = await this.command_dispatcher.dispatch('save.third', { third: party })
        return res.output
    }

    async save_company_as_party(party:Party): Promise<{ status: string, message?: string|Party }> {
        let res = await this.command_dispatcher.dispatch('save.company.as.party', { supplier: party })
        return res.output
    }

    async get_pdf_info(uuid:string) : Promise<Object> {
        let queryURL = `/e_invoice_to_pdf/?uuid=${uuid}`;
        let response = await axios.get(process.env.VUE_APP_API_ENDPOINT+queryURL, {})
        if (response) {
            return {
                'invoiceable': response.data.invoiceable? this.api_to_domain(response.data.invoiceable) : null,
                'personalization': response.data.personalization,
                'rectificative_e_invoice': response.data.rectificative_e_invoice? this.api_to_domain(response.data.rectificative_e_invoice) : null,
                'product_configuration': response.data.product_configuration? response.data.product_configuration : null
            }
        } else {
            return {
                'invoiceable': null,
                'personalization': null,
                'rectificative_e_invoice': null,
                'product_configuration': null
            }
        }
    }

    async get_pdf_info_by_id(invoiceable_id: String) : Promise<Object> {
        let res = await this.query_dispatcher.dispatch('invoice.info.for.pdf', {invoiceable_id: invoiceable_id})
        return{
            'invoiceable': res.invoiceable? this.api_to_domain(res.invoiceable) : null,
            'personalization': res.personalization,
            'rectificative_e_invoice': res.rectificative_e_invoice? this.api_to_domain(res.rectificative_e_invoice) : null,
            'product_configuration': res.product_configuration? res.product_configuration : null
        }
    }

    async company_has_active_einvoice_contract(company_id:Number): Promise<boolean> {        
        let res = await this.query_dispatcher.dispatch('check.user.has.active.envoice.contract', {company_id: company_id})
        if (res.company) {
            return res.company
        }
        else {
            return false;
        }
    }

    async massive_send_via_email(ids: Array<number>, companydb_id: number, email_template: EmailTemplate): Promise<any> {
        let args = { inv_ids: ids, company_id: companydb_id, subject: email_template.subject, body: email_template.message }
        return await this.async_command_dispatcher.dispatch_with_result("massive.send.invoice.via.mail", args, 1000)
    }

    async massive_sign(ids: Array<number>): Promise<any> {
        //preparació dels dos arguments que requereix la comanda massive.rum.command que són: nom de la comanda a executar i una llista de diccionaris. per què aquests dos?:
        //si n = llista.length aleshores es cridarà la comanda "a executar" n vegades passant-hi com a args cada cop un dels n diccionaris de la llista.
        const params = ids.map(id => ({ inv_id: id })) //un exemple seria: [{'inv_id':4},{'inv_id':5},{'inv_id':6}]
        let args ={'cmd_to_run_name':'sign.static.invoice.xml', 'params' : params}
        let res = await this.async_command_dispatcher.dispatch("massive.run.command", args)
        return res 
    }

    async massive_payment_means(ids: Array<number>, payment_means: string): Promise<any> {
        const params = ids.map(id => ({ 
            inv_id: id,
            payment_means: payment_means 
        }))
        let args ={'cmd_to_run_name':'add.payment.means', 'params' : params}
        let res = await this.command_dispatcher.dispatch("massive.run.command", args)
        return res.output 
    }

    async massive_due_date(ids: Array<number>, due_date: string): Promise<any>{
        const params = ids.map(id => ({ 
            inv_id: id,
            due_date: due_date 
        }))
        let args ={'cmd_to_run_name':'add.due.date', 'params' : params}
        let res = await this.command_dispatcher.dispatch("massive.run.command", args)
        return res.output 
    }

    async massive_payment_means_due_date(ids: Array<number>, payment_means: string, due_date: string): Promise<any>{
        const params = ids.map(id => ({ 
            inv_id: id,
            payment_means: payment_means,
            due_date: due_date 
        }))
        let args ={'cmd_to_run_name':'add.payment.means.due.date', 'params' : params}
        let res = await this.command_dispatcher.dispatch("massive.run.command", args)
        return res.output 
    }

    async check_if_has_prev_draft_invoices(id: number): Promise<any> {
        let res = await this.query_dispatcher.dispatch('check.if.has.previous.draft.invoices', {inv_id: id})
        return res
    }

    async get_pending_email_invoices(company_id: number, invoiceable_type: string): Promise<Invoiceable[]> {
        let args = {
            company_id: company_id,
            limit: 1000, // High limit to get all pending invoices
            offset: 0,
            filters: {
                type: invoiceable_type,
                pending_email_send: true,
            },
            order_by: '-id',
            info_third: ''
        }
        let inv_data = await this.query_dispatcher.dispatch('get.facture.e.list', args)
        inv_data = inv_data.results || []
        let inv_list = []
        for (let inv in inv_data) {
            inv_list.push(this.api_to_domain(inv_data[inv]))
        }
        return inv_list
    }

    async massive_sign_to_draft(ids: Array<number>, delete_invoice_number: boolean = false): Promise<any> {
        const params = ids.map(id => ({ inv_id: id, delete_invoice_number: delete_invoice_number }))
        let args ={'cmd_to_run_name':'sign.to.draft.einvoice', 'params' : params}
        let res = await this.command_dispatcher.dispatch("massive.run.command", args)
        return res.output
    }

}